<template>
  <div class="security-user-edit-page">
    <div class="page-header">
      <span class="page-title">{{ isEdit ? '编辑安全员' : '安全员备案' }}</span>
      <a-button type="primary" @click="goBack">
        返回
        <template #icon><ArrowLeftOutlined /></template>
      </a-button>
    </div>

    <div class="page-body">
      <a-spin :spinning="submitLoading">
        <a-form
          ref="formRef"
          :model="formData"
          :rules="rules"
          layout="vertical"
          class="edit-form"
        >
          <!-- 基本信息 -->
          <div class="form-section">
            <div class="section-title">基本信息</div>
            <a-row :gutter="24">
              <a-col :span="12">
                <a-form-item label="姓名" name="name">
                  <a-input
                    v-model:value="formData.name"
                    placeholder="请输入姓名"
                    :maxlength="20"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="手机号码" name="phone">
                  <a-input
                    v-model:value="formData.phone"
                    placeholder="请输入手机号码"
                    :maxlength="11"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="身份证号" name="idCard">
                  <a-input
                    v-model:value="formData.idCard"
                    placeholder="请输入身份证号"
                    :maxlength="18"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="性别" name="gender">
                  <a-select
                    v-model:value="formData.gender"
                    placeholder="请选择性别"
                  >
                    <a-select-option :value="1">男</a-select-option>
                    <a-select-option :value="0">女</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="培训日期" name="trainingDate">
                  <a-date-picker
                    v-model:value="formData.trainingDate"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                    style="width: 100%"
                    placeholder="请选择培训日期"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                
              </a-col>
            </a-row>
          </div>

          <!-- 证件照片 -->
          <div class="form-section">
            <div class="section-title">证件照片</div>
            <a-row :gutter="48">
              <a-col :span="12">
                <a-form-item label="身份证正面" name="idPhotoFrontUrl">
                  <ImageUploader
                    v-model:imageUrl="formData.idPhotoFrontUrl"
                    pathName="securityUser/idCardFront"
                    :boxWidth="280"
                    :boxHeight="180"
                    uploadText="上传身份证正面"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="身份证反面" name="idPhotoBackUrl">
                  <ImageUploader
                    v-model:imageUrl="formData.idPhotoBackUrl"
                    pathName="securityUser/idCardBack"
                    :boxWidth="280"
                    :boxHeight="180"
                    uploadText="上传身份证反面"
                  />
                </a-form-item>
              </a-col>
            </a-row>
          </div>

          <!-- 培训证明附件 -->
          <div class="form-section">
            <div class="section-title">培训证明附件</div>
            <a-form-item label="培训证明" name="trainingCertFiles">
              <FileUpload
                ref="fileUploadRef"
                pathName="securityUser/trainingCert"
                updateTitle="上传培训证明"
                :maxCount="5"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                @uploadFinished="handleCertUpload"
                @fileListChange="handleCertListChange"
              />
            </a-form-item>
          </div>

          <!-- 提交按钮 -->
          <div class="form-actions">
            <a-button type="primary" :loading="submitLoading" @click="handleSubmit">
              {{ isEdit ? '保存' : '提交备案' }}
            </a-button>
            <a-button class="ml16" @click="goBack">取消</a-button>
          </div>
        </a-form>
      </a-spin>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { message } from "ant-design-vue";
import { ArrowLeftOutlined } from "@ant-design/icons-vue";
import ImageUploader from "@/components/imageUploader/index.vue";
import FileUpload from "@/components/fileUpload/index.vue";
import useUserStore from "@/store/modules/user";
import {
  addSecurityUser,
  updateSecurityUser,
  getSecurityUserDetail,
} from "@/api/securityUser/securityUser";

const router = useRouter();
const route = useRoute();
const store = useUserStore();
const formRef = ref();
const fileUploadRef = ref();
const submitLoading = ref(false);

// 编辑模式：路由带 id 时为编辑，否则为新增
const userId = computed(() => route.query.id || null);
const isEdit = computed(() => !!userId.value);

// 表单数据
const formData = reactive({
  name: "",
  phone: "",
  idCard: "",
  gender: undefined,
  trainingDate: undefined,
  status: 1,
  idPhotoFrontUrl: "",
  idPhotoBackUrl: "",
  trainingCertFiles: [],
});

// 表单校验规则
const rules = {
  name: [{ required: true, message: "请输入姓名", trigger: "blur" }],
  phone: [
    { required: true, message: "请输入手机号码", trigger: "blur" },
    {
      pattern: /^1[3-9]\d{9}$/,
      message: "请输入正确的手机号码",
      trigger: "blur",
    },
  ],
  idCard: [
    { required: true, message: "请输入身份证号", trigger: "blur" },
    {
      pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
      message: "请输入正确的身份证号",
      trigger: "blur",
    },
  ],
  gender: [{ required: true, message: "请选择性别", trigger: "change" }],
  trainingDate: [
    { required: true, message: "请选择培训日期", trigger: "change" },
  ],
  idPhotoFrontUrl: [
    { required: true, message: "请上传身份证正面照片", trigger: "change" },
  ],
  idPhotoBackUrl: [
    { required: true, message: "请上传身份证反面照片", trigger: "change" },
  ],
};

// 培训证明上传完成回调（单个文件上传成功）
const handleCertUpload = () => {
  // FileUpload 组件已通过 fileListChange 同步文件列表，此处仅处理额外逻辑
};

// 培训证明文件列表变化回调
const handleCertListChange = (fileList) => {
  formData.trainingCertFiles = fileList
    .filter((f) => f.status === "done" && f.url)
    .map((f) => ({ fileName: f.name, fileUrl: f.url }));
};

// 提交
const handleSubmit = async () => {
  // try {
  //   await formRef.value.validate();
  // } catch (e) {
  //   message.warning("请完善必填项");
  //   return;
  // }
  console.log(formData);
  
  submitLoading.value = true;
  try {
    const payload = {
      ...formData,
      deptId: store.deptId,
    };

    // 编辑模式携带 id 并调用更新接口，新增模式调用新增接口
    const isEditMode = isEdit.value;
    if (isEditMode) {
      payload.id = userId.value;
    }
    const res = isEditMode
      ? await updateSecurityUser(payload)
      : await addSecurityUser(payload);
    if (res.code === 200) {
      message.success(isEditMode ? "更新成功" : "备案成功");
      goBack();
    } else {
      message.error(res.msg || (isEditMode ? "更新失败" : "备案失败"));
    }
  } catch (error) {
    console.error("提交失败:", error);
    message.error("提交失败");
  } finally {
    submitLoading.value = false;
  }
};

// 加载安全员详情（编辑模式回显）
const loadDetail = async () => {
  const id = userId.value;
  if (!id) return;
  try {
    const res = await getSecurityUserDetail(id);
    if (res.code === 200 && res.data) {
      const detail = res.data;
      formData.name = detail.name || "";
      formData.phone = detail.phone || "";
      formData.idCard = detail.idCard || "";
      formData.gender = detail.gender;
      formData.trainingDate = detail.trainingDate || undefined;
      formData.status = detail.status ?? 1;
      formData.idPhotoFrontUrl = detail.idPhotoFrontUrl || "";
      formData.idPhotoBackUrl  = detail.idPhotoBackUrl || "";

      // 培训证明文件列表回显
      if (Array.isArray(detail.trainingCertFiles) && detail.trainingCertFiles.length > 0) {
        const files = detail.trainingCertFiles.map((f) => ({
          uid: f.id || f.fileUrl,
          name: f.fileName,
          status: "done",
          url: f.fileUrl,
        }));
        fileUploadRef.value?.setFileList(files);
        formData.trainingCertFiles = detail.trainingCertFiles.map((f) => ({
          fileName: f.fileName,
          fileUrl: f.fileUrl,
        }));
      }
    } else {
      message.error(res.message || "获取详情失败");
    }
  } catch (error) {
    console.error("获取安全员详情失败:", error);
    message.error("获取安全员详情失败");
  }
};

const goBack = () => {
  router.back();
};

onMounted(() => {
  if (isEdit.value) {
    loadDetail();
  }
});
</script>

<style scoped lang="less">
.security-user-edit-page {
  display: flex;
  flex-direction: column;
  padding: 16px;
  height: calc(100vh - 70px);
  background-color: #ffffff;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
  flex-shrink: 0;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
  }
}

.page-body {
  flex: 1;
  overflow-y: auto;
}

.edit-form {
  max-width: 800px;
  margin: 0 auto;
}

.form-section {
  margin-bottom: 32px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
    line-height: 1;
  }
}

.form-actions {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.ml16 {
  margin-left: 16px;
}
</style>
